import { flexgFontFamily } from "@dashboard/theme/flexgPalette";
import { type Theme } from "@material-ui/core/styles";
import { dark, light, type Themes } from "@saleor/macaw-ui";
import { themes, vars } from "@saleor/macaw-ui-next";

const breakpoints = {
  keys: ["xs", "sm", "md", "lg", "xl"],
  values: { lg: 1680, md: 1280, sm: 600, xl: 1920, xs: 0 },
} as unknown as Theme["breakpoints"];

/** 라디오·체크박스의 치수. 두 컴포넌트가 legacy macaw 에서 같은 값을 쓴다. */
const CONTROL_SIZE = {
  padding: 6,
  "& svg": {
    width: 16,
    height: 16,
  },
  "& input": {
    width: 28,
    height: 28,
    top: -6,
    left: -6,
  },
  "& > span::before": {
    width: 16,
    height: 16,
  },
};

export const themeOverrides: Partial<Theme> = {
  breakpoints,
  // MUI v4 컴포넌트는 상속에 기대지 않고 theme.typography.fontFamily 를 제 스타일에 직접
  // 박는다. 비워 두면 MUI 기본값(Roboto/Arial)이라 한글이 macaw 쪽과 다른 글꼴로 찍힌다.
  // macaw 의 ThemeProvider 가 이 객체를 깊은 병합하므로 fontFamily 만 넘겨도 된다.
  // (2026-09-17 KST 추가)
  typography: { fontFamily: flexgFontFamily } as Theme["typography"],
  overrides: {
    MuiCard: {
      root: {
        border: 0,
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiTypography: {
      // 크기·굵기가 px/숫자로 박혀 있어 루트 rem 축소나 macaw 토큰을 따라오지 않는다.
      // macaw-next 쪽 기준값(12px)과 굵기 토큰에 맞춘다. (2026-09-17 KST 수정)
      body2: {
        fontSize: vars.fontSize[3],
      },
      caption: {
        fontSize: vars.fontSize[3],
        // MUI 는 number 로 강제하지만 여기서 넘기는 건 CSS 변수다
        fontWeight: vars.fontWeight.medium as unknown as number,
      },
    },
    MuiTextField: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiInputBase: {
      root: {
        borderColor: vars.colors.border.default1,
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: vars.colors.background.default1,
        color: vars.colors.text.default1,
        "&:hover $notchedOutline": {
          borderColor: vars.colors.border.default1,
        },
        "&:hover": {
          boxShadow: "none !important",
        },
      },
      notchedOutline: {
        borderColor: vars.colors.border.default1,
      },
    },
    MuiInputLabel: {
      root: {
        color: vars.colors.text.default1,
      },
    },
    MuiCardHeader: {
      root: {
        paddingRight: vars.spacing[8],
        backgroundColor: vars.colors.background.default1,
      },
      title: {
        fontSize: vars.fontSize[5],
        // MUI it strictly typed to be a number, but we are passing CSS variable here
        fontWeight: vars.fontWeight.medium as unknown as number,
        lineHeight: vars.lineHeight[5],
        letterSpacing: vars.letterSpacing[5],
      },
    },
    MuiCardContent: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: vars.colors.background.default1,
        color: vars.colors.text.default1,
      },
    },
    MuiTable: {
      root: {
        tableLayout: "auto !important" as "auto",
      },
    },
    // ResponsiveTable 을 거치지 않는 맨 MUI 표에도 같은 머리글 배경을 준다
    // (2026-09-18 KST 추가)
    MuiTableHead: {
      root: {
        backgroundColor: vars.colors.background.default2,
      },
    },
    /*
     * 짝수 행 줄무늬 (2026-09-18 KST 추가).
     *
     * tbody 안에서 세므로 첫 데이터 행이 흰색, 둘째가 회색이 된다. 머리글 행은
     * thead 의 첫 행이라 홀수로 잡혀 여기 안 걸리고, 위 MuiTableHead 가 칠한다.
     *
     * MuiTableBody 에 `& > tr` 로 넣으면 선택자가 한 단계 길어져 MUI 의 hover·selected
     * 규칙을 이겨버린다. MuiTableRow 에 두면 (0,1,1) 이라 그 둘이 그대로 이긴다.
     */
    MuiTableRow: {
      root: {
        "&:nth-child(even)": {
          backgroundColor: vars.colors.background.default2,
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottomColor: vars.colors.border.default1,
      },
    },
    MuiIconButton: {
      edgeEnd: {
        marginRight: 0,
      },
    },
    /*
     * 라디오·체크박스를 줄인다 (2026-09-22 KST 추가).
     *
     * legacy macaw 0.7 은 둘 다 20px 표시에 여백 14px 을 둘러 48px 짜리 덩어리로 만든다.
     * 글자를 12px 로 내린 지금은 표시가 글줄보다 커서 눈에 먼저 들어온다.
     * 16px 에 여백 6px 로 28px 까지 줄인다.
     *
     * 값 네 개가 같이 움직여야 한다. svg 가 보이는 표시, padding 이 그 둘레,
     * input 은 누를 수 있는 넓이라 (표시 + 여백×2)와 같아야 하고 그 자리는 여백만큼 음수로
     * 당겨 맞춘다. span::before 는 마우스를 올렸을 때 퍼지는 원이라 표시와 크기가 같아야
     * 한쪽으로 삐져나오지 않는다.
     */
    MuiRadio: {
      root: CONTROL_SIZE,
    },
    MuiCheckbox: {
      root: CONTROL_SIZE,
    },
    // 라벨을 왼쪽으로 당기는 값은 위 여백(padding)과 같아야 표시가 카드 안쪽 선에 붙는다.
    // legacy macaw 는 여백 14px 에 맞춘 -14px 라 여백을 6px 로 줄인 뒤 8px 씩 삐져나왔다.
    // (2026-10-01 KST 추가)
    MuiFormControlLabel: {
      root: {
        marginLeft: -CONTROL_SIZE.padding,
      },
    },
    MuiCardActions: {
      root: {
        backgroundColor: vars.colors.background.default1,
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    MuiFormHelperText: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiDialogTitle: {
      root: {
        fontSize: vars.fontSize[5],
        // MUI it strictly typed to be a number, but we are passing CSS variable here
        fontWeight: vars.fontWeight.medium as unknown as number,
        lineHeight: vars.lineHeight[5],
        letterSpacing: vars.letterSpacing[5],
      },
    },
  },
};

export const paletteOverrides: Themes = {
  light: {
    ...light,
    background: {
      ...light.background,
      default: themes.defaultLight.colors.background.default1,
    },
  },
  dark: {
    ...dark,
    background: {
      ...dark.background,
      default: themes.defaultDark.colors.background.default1,
    },
  },
};
